"use client";

import React from "react";
import Image from "next/image";
import {
  HeartPulse,
  TestTubeDiagonal as TestTube,
  Leaf,
  MessageSquare,
  GraduationCap,
  ShieldCheck,
  CalendarDays,
  CalendarClock,
  Sparkles,
} from "lucide-react";
import { useCart } from "@data/CartContext";
import { track } from "@lib/track";
import { FAQ_ITEMS } from "./faqItems";
import { CLEANSE_PRICE, upcomingGuidedIntakes, type GuidedIntake } from "./cleanseDates";

// COLOUR RULE FOR THIS FILE: no hardcoded Tailwind colour utilities for text or
// surfaces — use the --color-* alias family (or --bg-page) so both halves flip
// together.
//
// This page was built against a light-only template: theme tokens for the
// backgrounds (--color-bg-muted, --color-accent, --color-brand) but literal
// slate/white for the text. Under `prefers-color-scheme: dark`,
// --color-bg-muted becomes #211915 while text-slate-700 stays dark, so every
// card on the page — testimonials, FAQ, pricing, dates, "what you get" —
// rendered near-black text on a near-black card for anyone whose OS is in dark
// mode. The site's own header and footer wrap this page and were switching
// correctly, which is what made it look like a page-specific fault.
//
// The `text-white` classes that remain are deliberate: each sits on a
// --color-brand or gradient fill, which is dark in BOTH themes. So are the
// emerald status pills — a light chip with dark text is internally consistent
// either way.

// -----------------------------------------------------------------------------
// Shared data
// -----------------------------------------------------------------------------

const CHECKOUT_URL = "/shop/cart";
const PRICE_LABEL = `£${CLEANSE_PRICE}`;

// Every live-only claim on this page hangs off `hasLive`. With no upcoming
// intake in cleanseDates.ts the page must not promise nightly Zooms, must not
// link to a dates section that has nothing in it, and must sell the self-paced
// cleanse first — see the section ordering in ChunkTwo.

const INCLUDED_CORE = [
  { icon: <Leaf className="h-6 w-6" />, title: "Juices and broths", desc: "Four days of juices and mineral-rich broths. No juicer needed." },
  { icon: <Sparkles className="h-6 w-6" />, title: "Two days of real food", desc: "Transition recipes and a shopping list, so you come off the cleanse properly rather than falling off it." },
  { icon: <TestTube className="h-6 w-6" />, title: "Supplement pack", desc: "Herbs and nutrients for liver, lymph and digestion, dosed for the six days." },
  { icon: <GraduationCap className="h-6 w-6" />, title: "The why, not just the what", desc: "How detox pathways actually work, where your toxic load comes from, parasite basics, and how to choose supplements without guessing." },
  { icon: <ShieldCheck className="h-6 w-6" />, title: "After the six days", desc: "A month of probiotics, the Get Up & Go cacao blend and a plan for the week that follows." },
];

const INCLUDED_LIVE = {
  icon: <MessageSquare className="h-6 w-6" />,
  title: "Niki, every day",
  desc: "A private WhatsApp group for the week and a live Zoom with Niki each evening.",
};

const INCLUDED_SELF_PACED = {
  icon: <CalendarClock className="h-6 w-6" />,
  title: "Start when you like",
  desc: "The full six-day protocol, day by day, so you can run it around your own week instead of waiting for an intake.",
};

const TOPICS = [
  "The science of cleansing & how your body detoxes",
  "Easy ways to reduce everyday toxic load",
  "Optimising liver detox pathways naturally",
  "Identifying your primary health priorities",
  "Simple strategies to support healing",
  "Parasite cleansing: what, why, when",
  "How to choose supplements wisely",
];

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
export default function FeaturePageClient() {
  const intakes = upcomingGuidedIntakes();
  const hasLive = intakes.length > 0;
  // Where "buy" points. With no live intake the only thing on sale is self-paced.
  const buyHref = hasLive ? "#dates" : "#self-paced";

  return (
    // Not <main> — the root layout already renders one around this page.
    <div className="min-h-screen bg-gradient-to-b from-[var(--color-bg-muted)] via-[var(--bg-page)] to-[var(--bg-page)] text-[var(--color-text-primary)]">
      <ChunkOne hasLive={hasLive} intakeCount={intakes.length} buyHref={buyHref} />
      <ChunkTwo hasLive={hasLive} intakes={intakes} />
      <ChunkThree hasLive={hasLive} buyHref={buyHref} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// CHUNK 1 — Header + Hero + TrustBar
// -----------------------------------------------------------------------------
// This page used to render its own sticky <header> here — a second nav bar with
// its own fake logo square and its own "Niki Charnock • 6-Day Cleanse" wordmark,
// sitting directly beneath the site header the root layout already renders. Two
// sticky bars ate most of a phone viewport, and the duplicate branding read as a
// different site. It was a landing-page-template leftover; the site header does
// the job. The in-page #program / #topics / #dates / #self-paced / #faq anchors
// are deliberately KEPT — FAQ answers and older links still point at them.
function ChunkOne({ hasLive, intakeCount, buyHref }: { hasLive: boolean; intakeCount: number; buyHref: string }) {
  return (
    <>
      <Hero hasLive={hasLive} intakeCount={intakeCount} buyHref={buyHref} />
      <TrustBar hasLive={hasLive} />
    </>
  );
}

function Hero({ hasLive, intakeCount, buyHref }: { hasLive: boolean; intakeCount: number; buyHref: string }) {
  return (
    <section className="relative">
      <div className="absolute inset-0 -z-10"><PatternBG /></div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <h1 className="font-serif text-[clamp(2.3rem,5vw,3.4rem)] font-light leading-[1.12] text-[var(--color-text-primary)]">
            A 6-Day Colon Cleanse
            <br /><span className="text-[var(--color-brand)]">you do at home</span>
          </h1>
          <p className="mt-5 text-lg text-[var(--color-text-primary)] max-w-xl">
            Six days of juices, broths, real food and targeted supplements, and a gentler alternative to colonic irrigation.{" "}
            {hasLive
              ? "Niki is in the WhatsApp group all week and on Zoom every evening."
              : "You get the whole protocol day by day and start on whichever day suits you."}
          </p>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            {[
              "Written by Niki Charnock, Naturopathic Nutritionist",
              hasLive ? "Daily WhatsApp and live Zoom" : "Start any day you like",
              "Juices, broths, meals and a post-cleanse plan",
            ].map((item) => (
              <li key={item} className="flex items-center gap-2 rounded-xl bg-[var(--color-bg-muted)]/70 p-3 shadow-sm ring-1 ring-[var(--color-accent)]"><HeartPulse className="h-4 w-4 text-[var(--color-brand)]" /> {item}</li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={buyHref}
              onClick={() => {
                // Label deliberately fixed at "Hero: See Dates" even when the
                // button reads "Start the cleanse" — it is the only series this
                // hero has (24 events in the JSONL corpus). meta.variant tells
                // the two states apart.
                track(
                  "cta_click",
                  { label: "Hero: See Dates", component: "Hero" },
                  { event: "cta_click", label: "Hero: See Dates", component: "Hero", meta: { variant: hasLive ? "live" : "self-paced" } }
                );
              }}
              className="inline-flex items-center justify-center rounded-full bg-[var(--color-brand)] px-6 py-3 font-medium text-white shadow hover:bg-[var(--btn-anchor-hover)]"
            >
              {hasLive ? "See dates" : "Start the cleanse"}
            </a>
          </div>
          <p className="mt-3 text-sm text-[var(--color-text-secondary)]">
            {hasLive
              ? (intakeCount === 1 ? "One intake open, and places are limited." : `${intakeCount} intakes open, and places on each are limited.`)
              : "No live intake dates are scheduled right now. The self-paced cleanse starts whenever you do."}
          </p>
        </div>
        <div className="relative flex justify-center">
          <div className="absolute -inset-6 -z-10 rounded-3xl bg-[var(--color-bg-muted)] blur-2xl" />
          <div className="text-center p-5">
            <Image
              src="/images/retreats/nikiFood.webp"
              alt="Juices, broths and whole foods from the 6-day cleanse"
              // Source is 694x900, so a 560px circle is still inside the
              // native resolution — no upscaling. The old markup was
              // h-3/4 w-3/4, and the h-3/4 did nothing (percentage height
              // against an auto-height parent), so the real size was 75% of the
              // grid column and nothing more.
              width={560}
              height={560}
              priority
              sizes="(max-width: 1024px) 90vw, 560px"
              className="mx-auto aspect-square w-full max-w-[560px] object-cover rounded-full"
            />
            <p className="text-sm text-[var(--color-text-secondary)] mt-5">Guided by</p>
            <p className="font-semibold text-[var(--color-brand)]">Niki Charnock</p>
            <p className="text-sm text-[var(--color-text-secondary)]">Naturopathic Nutritionist</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBar({ hasLive }: { hasLive: boolean }) {
  return (
    <section className="py-6 border-y border-[var(--color-accent)] bg-[var(--color-bg-muted)]/60">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm text-[var(--color-text-secondary)]">
        <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[var(--color-brand)]" /> Beginner friendly</div>
        <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[var(--color-brand)]" /> {hasLive ? "6 days, live support" : "6 days, start any time"}</div>
        <div className="flex items-center gap-2"><Leaf className="h-4 w-4 text-[var(--color-brand)]" /> Whole-food based</div>
        <div className="flex items-center gap-2"><HeartPulse className="h-4 w-4 text-[var(--color-brand)]" /> No juicer needed</div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// CHUNK 2 — Core Content Sections
// -----------------------------------------------------------------------------
function ChunkTwo({ hasLive, intakes }: { hasLive: boolean; intakes: GuidedIntake[] }) {
  return (
    <>
      <IncludedSection hasLive={hasLive} />
      <TopicsSection hasLive={hasLive} />
      {/* With live intakes on sale the dates come first, because picking one is
          the main action. With none, the self-paced cleanse is the only thing
          that can be bought, so it leads and the live note follows it. */}
      {hasLive ? (
        <>
          <DatesSection intakes={intakes} />
          <SelfSection soleOffer={false} />
        </>
      ) : (
        <>
          <SelfSection soleOffer />
          <NoDatesSection />
        </>
      )}
      <WhySixDays hasLive={hasLive} />
    </>
  );
}

function IncludedSection({ hasLive }: { hasLive: boolean }) {
  const items = [...INCLUDED_CORE.slice(0, 3), hasLive ? INCLUDED_LIVE : INCLUDED_SELF_PACED, ...INCLUDED_CORE.slice(3)];
  return (
    <section id="program" className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="What you get"
          title="What arrives and what happens"
          subtitle={hasLive
            ? "Everything for the six days, plus daily contact with Niki while you do it."
            : "Everything for the six days, in one box, whenever you want to start."}
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <div key={item.title} className="rounded-2xl bg-[var(--color-bg-muted)] p-6 ring-1 ring-[var(--color-accent)] shadow-sm hover:shadow-md">
              <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--color-brand-hover)]">{item.icon}</div>
              <h3 className="font-serif text-lg font-normal text-[var(--color-brand)]">{item.title}</h3>
              <p className="mt-2 text-[var(--color-text-secondary)] text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TopicsSection({ hasLive }: { hasLive: boolean }) {
  return (
    <section id="topics" className="py-16 sm:py-20 bg-[var(--color-bg-surface)]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={hasLive ? "Live Zoom sessions" : "What's covered"}
          title="What you'll learn"
          subtitle={hasLive
            ? "One short session each evening with Niki, recorded if you can't make it."
            : "The ground the six days cover, so you know why you're doing each part."}
        />
        <div className="mx-auto max-w-3xl">
          <ul className="grid sm:grid-cols-2 gap-3">
            {TOPICS.map((t) => (
              <li key={t} className="flex items-start gap-3 rounded-xl bg-[var(--color-bg-muted)] p-4 ring-1 ring-[var(--color-accent)] shadow-sm">
                <div className="mt-1 h-2.5 w-2.5 rounded-full bg-[var(--color-brand)]" />
                <span className="text-[var(--color-text-secondary)]">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function DatesSection({ intakes }: { intakes: GuidedIntake[] }) {
  const { addToCart } = useCart();

  const handleAddToCart = (intake: GuidedIntake) => {
    const cartItem = {
      id: `guided-cleanse-${intake.range}`,
      title: `Guided Cleanse - Start Date: ${intake.range}`,
      price: PRICE_LABEL,
      qty: 1,
      totalPrice: CLEANSE_PRICE,
    };
    addToCart(cartItem);
    track(
      "add_to_cart",
      { label: `Added to Cart: ${intake.title}`, component: "DatesSection" },
      {
        event: "add_to_cart",
        label: `Added to Cart: ${intake.title}`,
        component: "DatesSection",
        meta: { range: intake.range, status: intake.status },
      }
    );
    window.location.href = CHECKOUT_URL;
  };

  return (
    <section id="dates" className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Live intakes"
          title="Pick your start date"
          subtitle="Small groups, so places are limited on each intake."
        />
        <div className={`mx-auto grid gap-3 ${intakes.length === 1 ? "max-w-xl" : intakes.length === 2 ? "md:grid-cols-2 max-w-3xl" : "md:grid-cols-3"}`}>
          {intakes.map((d) => (
            <div
              key={d.title}
              className="rounded-2xl bg-[var(--color-bg-muted)] p-6 ring-1 ring-[var(--color-accent)] shadow-sm hover:shadow-md"
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-[var(--color-brand)]">{d.title}</p>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                  <CalendarDays className="h-3.5 w-3.5" /> {d.status}
                </span>
              </div>
              <h3 className="mt-2 font-serif text-2xl font-light">{d.range}</h3>
              <button
                onClick={() => handleAddToCart(d)}
                className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[var(--color-brand)] px-4 py-2.5 text-[var(--btn-anchor-text)] font-medium shadow hover:bg-[var(--btn-anchor-hover)]"
              >
                Buy for this date
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Stands in for DatesSection when nothing is scheduled. It keeps the #dates
 * anchor alive (the nav, hero and closing CTA no longer point here, but old
 * links and the FAQ answers still can) and gives the live version somewhere to
 * go other than a dead end.
 */
function NoDatesSection() {
  return (
    <section id="dates" className="py-16 sm:py-20 bg-[var(--color-bg-surface)]">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl bg-[var(--color-bg-muted)] p-8 ring-1 ring-[var(--color-accent)] shadow-sm text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-[var(--color-brand)]">Live intakes</p>
          <h2 className="mt-2 font-serif text-[clamp(1.5rem,3vw,2.1rem)] font-light">No group dates scheduled at the moment</h2>
          <p className="mt-3 text-[var(--color-text-secondary)]">
            Niki runs the cleanse as a small live group a few times a year, with a WhatsApp group and a Zoom each
            evening. The next set of dates hasn&rsquo;t been announced. Tell us you&rsquo;re interested and we&rsquo;ll let you know when
            they open.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a
              href="/contactus"
              onClick={() => {
                track(
                  "cta_click",
                  { label: "Dates: Register Interest", component: "NoDatesSection" },
                  { event: "cta_click", label: "Dates: Register Interest", component: "NoDatesSection" }
                );
              }}
              className="inline-flex items-center justify-center rounded-full bg-[var(--color-brand)] px-6 py-3 text-white font-medium shadow hover:bg-[var(--btn-anchor-hover)]"
            >
              Tell me when dates open
            </a>
            <a href="#self-paced" className="inline-flex items-center justify-center rounded-full border border-[var(--color-brand)] px-6 py-3 font-medium hover:bg-[var(--color-brand)] hover:text-white">
              Start self-paced instead
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function SelfSection({ soleOffer }: { soleOffer: boolean }) {
  const { addToCart } = useCart();

  const handleAddToCart = () => {
    const cartItem = {
      id: "self-guided-cleanse",
      title: "6 Day Self Guided Cleanse",
      price: PRICE_LABEL,
      qty: 1,
      totalPrice: CLEANSE_PRICE,
    };
    addToCart(cartItem);
    track(
      "add_to_cart",
      { label: "Added to Cart: Self-Guided Cleanse", component: "SelfSection" },
      { event: "add_to_cart", label: "Added to Cart: Self-Guided Cleanse", component: "SelfSection" }
    );
    window.location.href = CHECKOUT_URL;
  };

  return (
    <section id="self-paced" className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={soleOffer ? "Available now" : "Any time"}
          title="The self-paced cleanse"
          subtitle="Recipes, shopping list and supplements arrive together. You choose the week and work through the six days in your own time."
        />
        {/* One card, so it gets a single column rather than sitting in a
            four-column grid looking like three are missing. */}
        <div className="mx-auto max-w-xl">
          <div className="rounded-2xl bg-[var(--color-bg-muted)] p-8 ring-1 ring-[var(--color-accent)] shadow-sm hover:shadow-md">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm font-medium text-[var(--color-brand)]">6 Day Self Guided Cleanse</p>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                <CalendarClock className="h-3.5 w-3.5" /> Start any day
              </span>
            </div>
            <h3 className="mt-2 font-serif text-3xl font-light">{PRICE_LABEL}</h3>
            <ul className="mt-4 space-y-2 text-sm text-[var(--color-text-secondary)]">
              <li>• Juices and broths for four days, no juicer needed</li>
              <li>• Two transition days of recipes and a shopping list</li>
              <li>• Six-day supplement pack for liver, lymph and digestion</li>
              <li>• A month of probiotics and the Get Up &amp; Go cacao blend</li>
            </ul>
            <button
              onClick={handleAddToCart}
              className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[var(--color-brand)] px-4 py-2.5 text-[var(--btn-anchor-text)] font-medium shadow hover:bg-[var(--btn-anchor-hover)]"
            >
              Buy Now
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhySixDays({ hasLive }: { hasLive: boolean }) {
  return (
    <section className="py-16 sm:py-20 bg-[var(--color-bg-surface)]">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl bg-gradient-to-br from-[var(--color-accent)] to-[var(--color-brand)] p-0.5 shadow">
          <div className="rounded-2xl bg-[var(--color-bg-muted)] p-8">
            <p className="text-xs font-semibold uppercase tracking-widest text-[var(--color-brand)]">Why six days</p>
            <h2 className="mt-2 font-serif text-[clamp(1.5rem,3vw,2.1rem)] font-light">Thorough, not extreme</h2>
            <p className="mt-3 text-[var(--color-text-secondary)] text-sm">
              Four days of juices and broths support both phases of liver detoxification while keeping the gut lining
              fed. The last two days bring you back onto real food, so you don&rsquo;t finish on Friday and undo it on
              Saturday.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
              <Badge>Food first</Badge>
              <Badge>{hasLive ? "Daily contact with Niki" : "Day-by-day plan"}</Badge>
              <Badge>Premium ingredients</Badge>
              <Badge>Beginner friendly</Badge>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// CHUNK 3 — Testimonials + FAQ + CTA
// -----------------------------------------------------------------------------
function ChunkThree({ hasLive, buyHref }: { hasLive: boolean; buyHref: string }) {
  return (
    <>
      <Testimonials />
      <Locations />
      <FAQ />
      <CTA hasLive={hasLive} buyHref={buyHref} />
    </>
  );
}

// The colonic query set is the highest-converting traffic this site gets
// ("colonic irrigation manchester" ranks ~6.8 at a 9.1% CTR; "colon cleanse
// central manchester" ranks ~2.0), and until now every one of those searches
// landed on the home page because this — the page that actually answers them —
// carried no place name at all. The cleanse itself is posted and done at home,
// so the honest local angle is the two clinics you can see Niki at, not a
// pretence that the programme is delivered in person.
//
// Nothing here claims colonic irrigation is offered. That distinction is the
// page's whole pitch and is stated in the FAQ and the Gut Buster post as well;
// keep the three in agreement.
function Locations() {
  const clinics = [
    {
      city: "Manchester",
      href: "/about/wellbeing-manchester",
      blurb:
        "Consultations in central Manchester, and the same 6-day cleanse posted to you wherever you are in Greater Manchester.",
    },
    {
      city: "Southport",
      href: "/about/wellbeing-southport",
      blurb:
        "Stocks House, Scarisbrick — consultations, remedial massage and Body Ballancer, with the cleanse posted out across Merseyside and West Lancashire.",
    },
  ];
  return (
    <section id="locations" className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Manchester, Southport & UK-wide"
          title="Looking for colonic irrigation near you?"
          subtitle="We don't offer colonic irrigation or colonic hydrotherapy at either clinic. This is the natural alternative: a guided colon cleanse you do at home, supporting your body's own elimination rather than flushing the colon with water in a clinic room."
        />
        <div className="mx-auto max-w-3xl text-[var(--color-text-secondary)] space-y-4">
          <p>
            The cleanse is posted to you and done at home, so you can start it anywhere in
            the UK — you don&apos;t need to be near a clinic to do it. What the clinics add is
            the option to sit down with Niki in person before or after, if you want your
            cleanse to sit inside a wider plan.
          </p>
        </div>
        <div className="mt-10 grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {clinics.map((c) => (
            <div key={c.city} className="rounded-2xl bg-[var(--color-bg-muted)] p-6 ring-1 ring-[var(--color-accent)] shadow-sm">
              <h3 className="font-serif text-xl font-light">{c.city}</h3>
              <p className="mt-2 text-sm text-[var(--color-text-secondary)]">{c.blurb}</p>
              <a
                href={c.href}
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[var(--color-brand)] hover:underline"
              >
                Visit the {c.city} clinic page
                <span aria-hidden="true">&rarr;</span>
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const items = [
    { quote: "I have done several heavy metal liver and gut cleanses with Niki. I always feel amazing afterwards ans notice my inflammation and Psoriasis improve over the week. She has made a something that seems quite overwhelming and complicated, very accessible and simple!", name: "Johnathan" },
    { quote: "The gut cleanse was unreal, felt so much better after & losing a few kgs was a great side note. Every bit of advice from Nic has made me feel healthier 🫶🏼✨", name: "Helen" },
    { quote: "The learning how to make the foods I need to support my gut and liver health has been invaluable and something I can incorporate into my regular nutrition. I have learned so much from Niki's expert guidance and the additional live sessions she provides as part of the support for the programme. I would highly recommend to anyone considering a reboot to provide the foundations and momentum for changes in nutritional habits.", name: "Claire" },
  ];
  return (
    <section className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="Results" title="What people who've done it say" />
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((t) => (
            <blockquote key={t.name} className="rounded-2xl bg-[var(--color-bg-muted)] p-6 ring-1 ring-[var(--color-accent)] shadow-sm">
              <p className="text-[var(--color-text-secondary)]">&ldquo;{t.quote}&rdquo;</p>
              <footer className="mt-4 text-sm font-medium">{t.name}</footer>
            </blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const items = FAQ_ITEMS;
  return (
    <section id="faq" className="py-16 sm:py-20 bg-[var(--color-bg-surface)]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading title="Frequently asked questions" />
        {/* Every question here is also in the FAQPage JSON-LD on the server
            page. Google forbids the markup for answers a visitor can't see, so
            if a question is ever hidden it has to leave faqItems.ts too. */}
        <div className="mx-auto max-w-3xl divide-y divide-[var(--color-accent)] rounded-2xl bg-[var(--color-bg-muted)] ring-1 ring-[var(--color-accent)]">
          {items.map((item, i) => (
            <details key={i} className="group p-6 open:bg-[var(--color-bg-sage-light)]/20">
              <summary className="flex cursor-pointer list-none items-center justify-between text-left font-medium">
                <span>{item.q}</span>
                <svg className="h-5 w-5 transition-transform group-open:rotate-180" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" /></svg>
              </summary>
              <p className="mt-3 text-[var(--color-text-secondary)] text-sm">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA({ hasLive, buyHref }: { hasLive: boolean; buyHref: string }) {
  return (
    <section className="py-16">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-3xl bg-gradient-to-tr from-[var(--color-brand)] to-[var(--color-accent)] p-8 sm:p-12 text-white shadow">
          <div className="absolute -inset-16 -z-10 opacity-30"><PatternBG dark /></div>
          <div className="max-w-3xl">
            <h3 className="font-serif text-[clamp(1.7rem,3.5vw,2.4rem)] font-light">Ready to start?</h3>
            <p className="mt-2 text-white/90">
              {hasLive
                ? "Pick an intake and Niki will take you through the six days."
                : "The self-paced cleanse is available now. Start on whichever day suits you."}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={buyHref} className="inline-flex items-center justify-center rounded-full border text-white border-white px-6 py-3 font-semibold hover:bg-white hover:text-[var(--color-brand)]">
                {hasLive ? "Choose a date" : "Start the cleanse"}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
function SectionHeading({ eyebrow, title, subtitle }: { eyebrow?: string; title: string; subtitle?: string }) {
  return (
    <div className="text-center mb-10">
      {eyebrow && <p className="text-xs font-semibold uppercase tracking-widest text-[var(--color-brand)]">{eyebrow}</p>}
      <h2 className="mt-2 font-serif text-[clamp(1.7rem,3.5vw,2.4rem)] font-light">{title}</h2>
      {subtitle && <p className="mt-3 max-w-2xl mx-auto text-[var(--color-text-secondary)]">{subtitle}</p>}
    </div>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return <span className="inline-flex items-center justify-center rounded-full bg-[var(--color-brand-hover)] px-3 py-1 text-xs font-medium text-[var(--color-text-secondary)]">{children}</span>;
}

function PatternBG({ dark = false }: { dark?: boolean }) {
  return (
    <svg aria-hidden className={`h-full w-full ${dark ? "text-white" : "text-[var(--color-brand)]"}`} xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
      <defs>
        <pattern id="hex" width="40" height="40" patternUnits="userSpaceOnUse" patternTransform="rotate(30)">
          <rect width="2" height="40" fill="currentColor" opacity="0.08" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#hex)" />
    </svg>
  );
}
